import Image from 'next/legacy/image';
import { ChangeEvent, useEffect, useState } from 'react';
import { Styled, TextInputProps } from '.';

/**
 * Props for TextInput component.
 * @param pattern - RegExp string for input validation.
 * @param onValidChange - Callback called with validity boolean on value change.
 * @param errMessage - Error message to display when validation fails.
 * @param name - Name of the input field.
 * @example
 * // Usage with validity tracking:
 * const { validity, handleValidChange, allValid } = useForm(['email', 'username']);
 * <TextInput
 *   name="email"
 *   pattern="^[^@]+@[^@]+\.[^@]+$"
 *   onValidChange={handleValidChange('email')}
 *   errMessage="Invalid email format"
 * />
 * <TextInput
 *  name="username"
 *  pattern="^[a-zA-Z0-9_]{3,}$"
 *  onValidChange={handleValidChange('username')}
 *  errMessage="Username must be at least 3 characters long and can only contain letters, numbers, and underscores"
 *  />
 */
const TextInput = ({
  name,
  label,
  value,
  placeholder,
  onChange,
  isError,
  errMessage = 'Error',
  disabled,
  optionHelperText,
  width,
  variant = 'grey',
  textArea = false,
  size = 'sm',
  height = '100px',
  inputIcon = '',
  maxLength = 50,
  onKeyDown,
  autoFocus,
  pattern,
  onValidChange,
}: TextInputProps) => {
  const [touched, setTouched] = useState<boolean>();
  const [validationError, setValidationError] = useState<string | null>(null);

  const getPatternValidation = (
    value: string,
    pattern: string | undefined
  ): boolean | null => {
    if (!pattern) return null;
    const isValid = RegExp(pattern).test(value);
    return isValid;
  };

  // Validate on initial render if pattern is provided. Without this, pre-filled values won't be validated until user changes them.
  useEffect(() => {
    if (pattern && value.length > 0) {
      const isValid = getPatternValidation(value, pattern) || false;
      setValidationError(!isValid ? errorMessage : null);
      onValidChange?.(isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeHandler = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setTouched(true);
    const val = event.target.value;

    if (pattern) {
      const isValid = getPatternValidation(val, pattern) || false;
      setValidationError(!isValid ? errorMessage : null);
      onValidChange?.(isValid); // .? makes it optional, and component does not crash if onValidChange is not provided
    }
    onChange(event);
  };

  const showError = pattern ? touched && !!validationError : touched && isError;
  const errorMessage = validationError || errMessage;

  return (
    <Styled.TextFieldContainer>
      {label?.length ? (
        <Styled.TextFieldLabel disabled={disabled}>
          {label}
        </Styled.TextFieldLabel>
      ) : null}
      {textArea ? (
        <Styled.TextArea
          value={value}
          onChange={onChangeHandler}
          placeholder={placeholder}
          disabled={disabled}
          isError={showError}
          width={width}
          variant={variant}
          height={height}
          displayWithIcon={false}
          onKeyDown={onKeyDown}
        />
      ) : (
        <Styled.TextFieldIconWrapper>
          {inputIcon && (
            <Styled.IconWrapper>
              <Image src={inputIcon} alt="icon" width="20" height="20" />
            </Styled.IconWrapper>
          )}

          <Styled.TextField
            name={name}
            value={value}
            onChange={onChangeHandler}
            placeholder={placeholder}
            disabled={disabled}
            isError={showError}
            width={width}
            variant={variant}
            fieldSize={size}
            displayWithIcon={inputIcon.length > 0}
            maxLength={maxLength}
            autoFocus={autoFocus}
            onKeyDown={onKeyDown}
          />
        </Styled.TextFieldIconWrapper>
      )}

      {showError && errorMessage && (
        <Styled.TextFieldErrorMessage>
          {errMessage}
        </Styled.TextFieldErrorMessage>
      )}
      {!showError && optionHelperText && optionHelperText.length > 0 && (
        <Styled.OptionalHelperText disabled={disabled}>
          {optionHelperText}
        </Styled.OptionalHelperText>
      )}
    </Styled.TextFieldContainer>
  );
};

export default TextInput;
